/**
 * Custom hook to manage track state
 */
import { Track, ParticipantEvent } from 'livekit-client';
import { useTracks } from '@livekit/components-react';
import { useEffect, useState, useCallback } from 'react';
import { useLogger } from '@ui/shared/components/Logger';
import { LOGGER_LABEL } from '../utils/constants';

/**
 * Hook to manage track state and track data
 *
 * @param {object} room - LiveKit room instance
 * @param {object} options - Optional configuration
 * @param {string} options.attributeToWatch - Metadata attribute to watch for changes
 * @param {function} options.onAttributeChange - Callback when attribute changes
 * @returns {object} - Track management data containing:
 *   - filteredTracks: Array of tracks excluding local participant
 *   - watchAttribute: Function to start watching a specific attribute
 *   - attributeValue: Current value of the watched attribute
 */
export const useTrackManagement = (room, options = {}) => {
  const { attributesToWatch, onAttributeChange } = options;
  const logger = useLogger();
  
  // This state will trigger re-renders when attributes change
  const [attributeChangeCounter, setAttributeChangeCounter] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Validate room on mount
  useEffect(() => {
    if (!room) {
      logger.error(LOGGER_LABEL, 'useTrackManagement: Room is not available');
      return;
    }

    if (!isInitialized) {
      setIsInitialized(true);
      logger.info(LOGGER_LABEL, 'useTrackManagement initialized', {
        roomName: room.name,
        roomState: room.state,
        hasAttributesToWatch: !!attributesToWatch,
        attributesToWatch
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, attributesToWatch]);
  
  // Get all tracks from LiveKit
  const allTracks = useTracks([
    Track.Source.Camera,
    Track.Source.ScreenShare,
    Track.Source.Unknown,
  ]);

  // Filter out local participant tracks
  let filteredTracks = [];
  try {
    if (!room?.localParticipant) {
      logger.warn(LOGGER_LABEL, 'Cannot filter tracks: local participant not available');
      filteredTracks = allTracks;
    } else {
      filteredTracks = allTracks.filter(
        (tr) => tr.participant.identity !== room.localParticipant.identity
      );
      
      logger.debug(LOGGER_LABEL, 'Tracks filtered', {
        totalTracks: allTracks.length,
        filteredTracks: filteredTracks.length,
        localParticipantIdentity: room.localParticipant.identity
      });
    }
  } catch (error) {
    logger.error(LOGGER_LABEL, 'Error filtering tracks', {
      error,
      errorMessage: error.message,
      totalTracks: allTracks.length
    });
    filteredTracks = allTracks;
  }
  
  // Function to watch for attribute changes on participants
  const watchAttribute = useCallback((attributeWatchArr) => {
    if (!room) {
      logger.warn(LOGGER_LABEL, 'Cannot watch attributes: room not available');
      return;
    }
    
    const handleAttributeChanged = (participant, changedKeys) => {
      try {
        // Get all the changedKeys that match the attributesToWatch array
        const matchedKeys = changedKeys.filter(key => attributesToWatch.includes(key));
        
        if(matchedKeys.length > 0){
          logger.debug(LOGGER_LABEL, 'Participant attributes changed', {
            participantIdentity: participant.identity,
            participantSid: participant.sid,
            matchedKeys,
            allChangedKeys: changedKeys
          });
          
          // Increment counter to trigger re-render
          setAttributeChangeCounter(prev => prev + 1);
          
          // Call the callback if provided
          if (onAttributeChange) {
            try {
              onAttributeChange(participant, matchedKeys);
            } catch (callbackError) {
              logger.error(LOGGER_LABEL, 'Error in onAttributeChange callback', {
                error: callbackError,
                errorMessage: callbackError.message,
                participantIdentity: participant.identity
              });
            }
          }
        }
      } catch (error) {
        logger.error(LOGGER_LABEL, 'Error processing attribute changes', {
          error,
          errorMessage: error.message,
          participantIdentity: participant?.identity,
          changedKeys
        });
      }
    };
    
    return handleAttributeChanged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, onAttributeChange, attributesToWatch]);
  
  // Set up event listeners for metadata changes if attributeToWatch is provided
  useEffect(() => {
    if (!room) {
      logger.warn(LOGGER_LABEL, 'Cannot setup attribute listeners: room not available');
      return;
    }
    
    if (!attributesToWatch) {
      logger.debug(LOGGER_LABEL, 'No attributes to watch, skipping listener setup');
      return;
    }
    
    logger.debug(LOGGER_LABEL, 'Setting up attribute change listeners', {
      attributesToWatch
    });
    
    const handleAttributeChanged = watchAttribute(attributesToWatch);
    
    try {
      room.on(ParticipantEvent.ParticipantAttributesChanged, handleAttributeChanged);
      
      logger.debug(LOGGER_LABEL, 'Attribute change listener registered');
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Failed to register attribute change listener', {
        error,
        errorMessage: error.message
      });
    }
    
    // Cleanup
    return () => {
      try {
        room.off(ParticipantEvent.ParticipantAttributesChanged, handleAttributeChanged);
        logger.debug(LOGGER_LABEL, 'Attribute change listener cleaned up');
      } catch (error) {
        logger.error(LOGGER_LABEL, 'Error cleaning up attribute change listener', {
          error,
          errorMessage: error.message
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, attributesToWatch, watchAttribute]);

  return {
    filteredTracks,
    watchAttribute,
    attributeChangeCounter
  };
};
